import { CommonModule } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { initFlowbite } from "flowbite";
import { KeycloakService } from "keycloak-angular";
import { filter, Subscription } from "rxjs";
import { LayoutModule } from "./layout/layout.module";
import { LoaderComponent } from "./shared/loader/component/loader/loader.component";
import { v4 as uuidv4 } from "uuid";
import { PushMobileTokenApiService } from "./services/generated/core-api/services/push-mobile-token-api.service";
import { SaveFcmToken$Params } from "./services/generated/core-api/fn/push-mobile-token-api/save-fcm-token";
import { AngularFireMessaging } from "@angular/fire/compat/messaging";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-root",
  standalone: true,
  imports: [CommonModule, LayoutModule, RouterOutlet, LoaderComponent],
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
})
export class AppComponent implements OnInit {
  private afMessaging = inject(AngularFireMessaging);
  private router = inject(Router);
  private messageSubscription?: Subscription;

  title = "backoffice-ic-front";
  routeReady = false;
  isPublic = false;
  isTeamInbox = false;
  private hasInitialized = false;

  constructor(
    private pushMobileTokenService: PushMobileTokenApiService,
    private translateService: TranslateService
  ) {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe((event) => {
        const url = event.urlAfterRedirects;
        this.isPublic = url.startsWith("/public");
        this.isTeamInbox = url.includes("/team-inbox/page");
        this.routeReady = true;

        if (!this.hasInitialized && !this.isPublic) {
          this.hasInitialized = true;
          this.initializeApp();
        }
      });
  }

  async ngOnInit(): Promise<void> {
    initFlowbite();
    // Clarity.init(environment.clarityProjectId);
  }

  private async initializeApp(): Promise<void> {
    this.requestPermission();
    this.listenForMessages();
    const sendTranslations = () => {
      this.translateService
        .getTranslation(this.translateService.currentLang || "fr")
        .subscribe((data) => {
          if (navigator.serviceWorker && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
              type: "translations",
              lang: this.translateService.currentLang || "fr",
              data: data,
            });
          }
        });
    };
    sendTranslations();
    this.translateService.onLangChange.subscribe(sendTranslations);
  }

  ngOnDestroy(): void {
    this.messageSubscription?.unsubscribe();
  }

  requestPermission(): void {
    Notification.requestPermission().then((permission) => {
      if (permission === "granted") {
        console.log("Notification permission granted.");
        this.afMessaging.requestToken.subscribe({
          next: (token) => {
            this.sendTokenToServer(token);
          },
          error: (error) => {
            console.error("Erreur lors de la récupération du token", error);
          },
        });
      } else {
        console.warn("Notification permission denied.");
      }
    });
  }

  sendTokenToServer(token: string | null): void {
    if (token == null) return;
    const currentUrl = this.router.url;
    const isPublicRoute =
      currentUrl.startsWith("/public") || currentUrl.includes("/public/");
    if (isPublicRoute) return;

    const saveFcmToken: SaveFcmToken$Params = {
      "x-api-canal": "web",
      "x-api-requestId": uuidv4(),
      body: { fcmToken: token },
    };
    this.pushMobileTokenService.saveFcmToken(saveFcmToken).subscribe({
      next: (response) => {
        //
      },
    });
  }

  private listenForMessages(): void {
    this.messageSubscription = this.afMessaging.messages.subscribe({
      next: (payload) => {
        // Mark as handled
        if (!payload.data) payload.data = {};
        payload.data["handled"] = "true";
        // Process notification if diff to /team-inbox/page
        if (this.router.url !== "/team-inbox/page") {
          this.showForegroundNotification(
            payload.data["title"] ||
            this.translateService.instant(
              "NOTIFICATION.NOTIFICATION_MSG.TITLE"
            ),
            payload.data["body"] ||
            this.translateService.instant(
              "NOTIFICATION.NOTIFICATION_MSG.BODY"
            )
          );
        }
      },
    });
  }

  private showForegroundNotification(title: string, body: string): void {
    if (Notification.permission !== "granted") return;
    const options: NotificationOptions = {
      body: this.formatNotifMessage(body),
      icon: "/icons/icon-192x192.png",
      data: {
        url: "/notifications",
        timestamp: Date.now(),
      },
      badge: "/favicon.ico",
    };
    const notification = new Notification(title, options);
    // Handle click event
    notification.onclick = (event) => {
      event.preventDefault();
      window.focus();
      this.router.navigateByUrl("/team-inbox/page");
      notification.close();
    };
    setTimeout(() => notification.close(), 10000);
  }

  formatNotifMessage(msg: string | undefined): string {
    if (!msg)
      return this.translateService.instant(
        "NOTIFICATION.NOTIFICATION_MSG.BODY"
      );
    const msgArr = msg.split(":");
    const type = msgArr.at(0)?.trim();
    const supportedTypes = [
      "IMAGE",
      "AUDIO",
      "VIDEO",
      "DOCUMENT",
      "INTERACTIVE",
      "ORDER",
      "CONTACTS",
      "LOCATION",
    ];
    if (!type || !supportedTypes.includes(type)) return msg;
    const icons: { [key: string]: string } = {
      IMAGE: "🖼️",
      AUDIO: "🎙️",
      VIDEO: "🎥",
      DOCUMENT: "📄",
      INTERACTIVE: "📲",
      ORDER: "🛒",
      CONTACTS: "👤",
      LOCATION: "📍",
    };
    const label =
      this.translateService.instant(`NOTIFICATION.NOTIFICATION_MSG.${type}`) ||
      type;
    return `${icons[type]} ${label}`;
  }

}
